'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { EnvironmentOutlined, StarFilled, CloseOutlined } from '@ant-design/icons';
import { Feature } from '../types';
import FeatureIcon from './FeatureIcon';

// Use the proxy-prefixed path for the fallback image since the app is served
// under /apps/campsite-explorer via Shopify proxy.  Requests to bare /images/…
// are NOT forwarded to the Next.js app, so we must include the proxy prefix.
const FALLBACK_IMAGE = '/apps/campsite-explorer/images/default-campsite-hero.jpg';

/**
 * Try to extract the original (non-Imgix) image URL from an Imgix web-proxy URL.
 * Imgix proxy URLs encode the original source as a URL-encoded path segment, e.g.:
 *   https://jola-888.imgix.net/https%3A%2F%2Fexample.com%2Fimg.jpg?auto=format
 * Returns the decoded original URL, or null if not an Imgix proxy URL.
 */
function extractOriginalFromImgix(url: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('imgix.net')) return null;
    const pathSegment = parsed.pathname.slice(1); // strip leading /
    if (!pathSegment) return null;
    const decoded = decodeURIComponent(pathSegment);
    if (/^https?:\/\//i.test(decoded)) {
      return decoded;
    }
  } catch {
    // not a valid URL – ignore
  }
  return null;
}

/** Campsite type color mapping */
const TYPE_COLORS: Record<string, string> = {
  'Free Camping': 'text-emerald-600',
  'Caravan Park': 'text-blue-600',
  'Holiday Park': 'text-purple-600',
  'Campsite': 'text-amber-700',
};

interface MapCampsiteCardProps {
  title: string;
  description: string;
  imageUrl: string;
  location?: string;
  isFree?: boolean;
  isPremium?: boolean;
  url?: string;
  onClose: () => void;
  onClick: () => void;
  onAddToTrip?: () => void;
  isInTrip?: boolean;
  rating?: string;
  campsiteType?: string;
  features?: Feature[];
}

const MapCampsiteCard: React.FC<MapCampsiteCardProps> = ({
  title,
  description: _description,
  imageUrl,
  location,
  isFree = false,
  isPremium = false,
  url,
  onClose,
  onClick,
  onAddToTrip,
  isInTrip = false,
  rating,
  campsiteType,
  features,
}) => {
  const [imgSrc, setImgSrc] = useState<string>(imageUrl || FALLBACK_IMAGE);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(
    !!imageUrl && imageUrl !== FALLBACK_IMAGE
  );
  const [fallbackAttempted, setFallbackAttempted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const newSrc = imageUrl || FALLBACK_IMAGE;
    setImgSrc(newSrc);
    setIsImageLoading(!!imageUrl && imageUrl !== FALLBACK_IMAGE);
    setFallbackAttempted(false);
  }, [imageUrl]);

  const handleImageError = useCallback((): void => {
    if (imgSrc === FALLBACK_IMAGE) return;

    // When the Imgix proxy URL fails, try the original source URL directly
    if (!fallbackAttempted) {
      const original = extractOriginalFromImgix(imgSrc);
      if (original) {
        setFallbackAttempted(true);
        setImgSrc(original);
        return;
      }
    }

    // Final fallback – default placeholder
    setImgSrc(FALLBACK_IMAGE);
    setIsImageLoading(false);
  }, [imgSrc, fallbackAttempted]);

  const handleCardClick = (): void => {
    if (url) {
      router.push(url);
    } else if (onClick) {
      onClick();
    }
  };

  const handleViewDetailsClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    handleCardClick();
  };

  const typeColorClass = useMemo(() => {
    if (!campsiteType) return 'text-gray-500';
    return TYPE_COLORS[campsiteType] || 'text-gray-500';
  }, [campsiteType]);

  const numericRating = rating ? parseFloat(rating) : null;
  const hasRating = numericRating !== null && !isNaN(numericRating) && numericRating > 0;

  const keyFeatures = useMemo(() => {
    if (!features || features.length === 0) return [];
    const priorityKeywords = [
      'pet', 'dog', 'shower', 'toilet', 'power', 'water',
      'wifi', 'pool', 'cabin', 'bbq', 'dump point', 'laundry',
    ];
    const withDesc = features.filter(f => f.desc);
    const priority = withDesc.filter(f =>
      priorityKeywords.some(kw => f.desc!.toLowerCase().includes(kw))
    );
    const rest = withDesc.filter(f =>
      !priorityKeywords.some(kw => f.desc!.toLowerCase().includes(kw))
    );
    return [...priority, ...rest].slice(0, 5);
  }, [features]);

  return (
    <div
      className="map-campsite-card w-full max-w-[300px] bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer relative"
    >
      {/* Image section */}
      <div className="relative w-full" style={{ paddingTop: '62.5%' /* 16:10 */ }}>
        <img
          src={imgSrc}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          onError={handleImageError}
          onLoad={() => setIsImageLoading(false)}
        />

        {/* Loading skeleton */}
        {isImageLoading && (
          <div
            className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200 via-slate-100 to-white"
            aria-hidden="true"
          />
        )}

        {/* Bottom gradient overlay for readability */}
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

        {/* Premium badge */}
        {isPremium && (
          <span className="absolute top-2 left-2 bg-amber-400 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm uppercase tracking-wide">
            Premium
          </span>
        )}

        {/* FREE badge */}
        {!isPremium && isFree && (
          <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm uppercase tracking-wide">
            Free
          </span>
        )}

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          aria-label="Close campsite details"
        >
          <CloseOutlined className="text-[10px]" />
        </button>
      </div>

      {/* Content section */}
      <div className="px-3 py-2.5" onClick={handleCardClick}>
        {/* Type + Rating row */}
        {(campsiteType || hasRating) && (
          <div className="flex items-center gap-1.5 mb-1">
            {campsiteType && (
              <span className={`text-[11px] font-semibold ${typeColorClass}`}>
                {campsiteType}
              </span>
            )}
            {campsiteType && hasRating && (
              <span className="text-gray-300 text-[10px]">·</span>
            )}
            {hasRating && (
              <span className="flex items-center gap-0.5 text-[11px] text-amber-600 font-medium">
                <StarFilled className="text-[10px]" />
                {numericRating!.toFixed(1)}
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="text-[13px] font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">
          {title}
        </h3>

        {/* Location */}
        {location && (
          <div className="flex items-center gap-1 mb-2 text-[11px] text-gray-500">
            <EnvironmentOutlined className="text-[10px] text-red-400 flex-shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}

        {/* Feature icons */}
        {keyFeatures.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2.5">
            {keyFeatures.map((feature) => (
              <div
                key={feature.id}
                className="w-6 h-6 flex items-center justify-center bg-gray-50 rounded-full border border-gray-100"
                title={feature.desc || ''}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <FeatureIcon feature={feature} size="sm" />
                </span>
              </div>
            ))}
            {features && features.filter(f => f.desc).length > 5 && (
              <span className="text-[10px] text-gray-400 font-medium ml-0.5">
                +{features.filter(f => f.desc).length - 5}
              </span>
            )}
          </div>
        )}

        {/* Action buttons - side by side */}
        <div className="flex gap-1.5">
          {onAddToTrip && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToTrip();
              }}
              className={`flex-1 py-1.5 px-2 text-[11px] font-semibold rounded-md transition-all text-white shadow-sm ${
                isInTrip
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isInTrip ? '✓ Added' : '+ Trip'}
            </button>
          )}

          <button
            onClick={handleViewDetailsClick}
            className={`py-1.5 px-2 text-[11px] font-semibold rounded-md border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-all ${
              onAddToTrip ? 'flex-1' : 'w-full'
            }`}
            aria-label={`View details for ${title}`}
          >
            Details →
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapCampsiteCard;
